import {
  OmniElement,
  OmniIconElement,
  OmniSwitchElement,
  OmniStyleElement,
  OmniToolbarElement,
  OmniTileElement,
  OmniTooltipElement,
  repeat,
  html,
  css,
} from 'omni-ui';

import * as bpConstants from '../../services/blueprint-graph/constants.js';
import { ToolboxButtonSvgTemplate } from './templates/toolbox-button-svg.js';

OmniStyleElement.register();
OmniSwitchElement.register();
OmniToolbarElement.register();
OmniTileElement.register();
OmniTooltipElement.register();
OmniIconElement.register();

export class CampaignBlueprintToolbox extends OmniElement {
  static styles = [
    super.styles,
    css`
      :host {
        --toolbox-spacing: 2.143rem;
        padding: 1.7rem 0;
      }

      .toolbox-body {
        overflow-y: auto;
        padding: 0 var(--toolbox-spacing) var(--toolbox-spacing)
          var(--toolbox-spacing);
      }

      .toolbox-button-group {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        align-items: flex-start;
      }

      .toolbox-group:not(:last-child) {
        padding-bottom: var(--toolbox-spacing);
        margin-bottom: var(--toolbox-spacing);
        border-bottom: thin solid var(--color-pale-grey);
      }

      .toolbox-button-container {
        flex: 0 0 25%;
      }

      button.toolbox-button {
        display: flex;
        flex-direction: column;
        justify-content: stretch;
        align-items: stretch;
        width: 100%;
        aspect-ratio: 1;
        border: none;
        height: auto;
        padding: 0;
        background: none;
        transition: all 0.3s ease;
        cursor: grab;
      }

      button.toolbox-button:hover,
      button.toolbox-button:active,
      button.toolbox-button:focus {
        background: none;
      }
      button.toolbox-button:focus {
        outline: thin solid rgba(var(--rgb-primary), 0.25);
      }
      button.toolbox-button:active {
        cursor: grabbing;
      }

      button.toolbox-button.dragging:focus {
        outline: none;
      }

      .toolbox-button-icon {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 100%;
        height: 100%;
      }

      button.toolbox-button svg {
        width: 50%;
        aspect-ratio: 1;
      }

      .toolbox-button-shape,
      .toolbox-button-line,
      .toolbox-button-image {
        fill: rgba(var(--rgb-primary), 0.5);
      }
      .toolbox-button-text {
        fill: var(--color-primary);
        font-weight: bold;
        font-size: 2rem;
      }
    `,
  ];

  /**
   * @todo: For managing the toolbox button drag events?
   */
  #handleDrag = (e, data) => {
    e.stopPropagation();
    e.target.classList.add('dragging');
    e.dataTransfer.setData('application/json', JSON.stringify(data));
  };

  /** stop tile from dragging */
  #cancelPointerEvents = e => {
    e.stopPropagation();
  };

  #renderButton = (clickHandler, iconContent, type) => html`
    <div class="toolbox-button-container">
      <button
        @dragstart=${e => this.#handleDrag(e, type)}
        @dragend=${e => e.target.classList.remove('dragging')}
        @click=${clickHandler}
        @pointerdown=${this.#cancelPointerEvents}
        @pointermove=${this.#cancelPointerEvents}
        @pointerup=${this.#cancelPointerEvents}
        @pointerout=${this.#cancelPointerEvents}
        class="toolbox-button"
        draggable="true"
      >
        <div class="toolbox-button-icon">${iconContent}</div>
      </button>
    </div>
  `;

  render() {
    return html`
      <omni-style>
        <div class="toolbox-body">
          <!-- Shape Vertices -->
          <div class="toolbox-group">
            <label class="label ml-0 has-text-weight-bold">Shapes</label>
            <div class="toolbox-button-group">
              ${repeat(
                Object.keys(bpConstants.SHAPE_TYPES),
                shape => shape,
                shape =>
                  this.#renderButton(
                    () => {
                      this.dispatchNewEvent('add-shape', {
                        detail: { type: shape },
                      });
                    },
                    ToolboxButtonSvgTemplate(shape),
                    { type: 'shape', shape },
                  ),
              )}
            </div>
          </div>

          <!-- Edges -->
          <div class="toolbox-group">
            <label class="label ml-0 has-text-weight-bold">Lines</label>
            <div class="toolbox-button-group">
              ${this.#renderButton(
                () => {
                  this.dispatchNewEvent('add-edge');
                },
                ToolboxButtonSvgTemplate('line'),
                { type: 'line', style: 'normal' },
              )}
              ${this.#renderButton(
                () => {
                  this.dispatchNewEvent('add-edge');
                },
                ToolboxButtonSvgTemplate('dashedLine'),
                { type: 'line', style: 'dashed' },
              )}
              ${this.#renderButton(
                () => {
                  this.dispatchNewEvent('add-edge');
                },
                ToolboxButtonSvgTemplate('thickLine'),
                { type: 'line', style: 'thick' },
              )}
              ${this.#renderButton(
                () => {
                  this.dispatchNewEvent('add-edge');
                },
                ToolboxButtonSvgTemplate('lineArrow'),
                { type: 'line', style: 'normal', arrow: true },
              )}
            </div>
          </div>

          <!-- Non-shape vertices -->
          <div class="toolbox-group">
            <label class="label ml-0 has-text-weight-bold"
              >Text and Image</label
            >
            <div class="toolbox-button-group">
              ${this.#renderButton(
                () => {
                  this.dispatchNewEvent('add-text');
                },
                ToolboxButtonSvgTemplate('text'),
                { type: 'text' },
              )}
              ${this.#renderButton(
                () => {
                  this.dispatchNewEvent('add-image');
                },
                ToolboxButtonSvgTemplate('image'),
                { type: 'image' },
              )}
            </div>
          </div>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-blueprint-toolbox', CampaignBlueprintToolbox);
